import { RankedTeam } from "@/api/generated/backendAPI.schemas";
import { formatTimeAgo, getShortenedScore } from "@/utils/utils";
import { BiSolidMedal } from "react-icons/bi";
import "@/components/components.scss";
import HelpTip from "../ui/HelpTip";

type Props = {
  rankingData: readonly RankedTeam[];
};

const thStyle =
  "px-4 py-3 text-center text-[#111518] text-sm font-medium leading-normal whitespace-nowrap";
const tdStyle = "h-[72px] px-4 py-2 text-sm font-normal leading-normal";
const MEDAL_COLORS = ["#FFD700", "#C0C0C0", "#DA744B"];

/**
 * チームのリーダーボード。チームのスコアはメンバーのベストのうち最良
 * （バックエンドが今のメンバーから計算する）。
 */
function TeamRanking({ rankingData }: Props) {
  return (
    <div className="px-4 py-3 @container">
      <div className="flex overflow-hidden rounded-xl border border-[#dbe1e6] bg-white">
        <table className="flex-1">
          <thead>
            <tr className="bg-white">
              <th className={"w-[12%] " + thStyle}>ランク</th>
              <th className={"w-[15%] " + thStyle}>チーム</th>
              <th className={thStyle}>メンバー</th>
              <th className={"w-[12%] " + thStyle}>
                スコア
                <HelpTip text="メンバーそれぞれのベストスコアのうち、最も良いものです。" />
              </th>
              <th className={"w-[10%] " + thStyle}>投稿回数</th>
              <th className={"w-[10%] " + thStyle}>最終投稿</th>
            </tr>
          </thead>
          <tbody>
            {rankingData.map((team) => {
              const isMine = team.members.some((m) => m.isMe);
              return (
                <tr
                  key={team.teamId}
                  className={
                    "border-t border-t-[#dbe1e6]" +
                    (isMine ? " bg-[#fdf6e3]" : "")
                  }
                >
                  <td className={tdStyle}>
                    <div className="flex items-center justify-center gap-4">
                      {team.rankNum <= 3 ? (
                        <BiSolidMedal
                          color={MEDAL_COLORS[team.rankNum - 1]}
                          size={24}
                        />
                      ) : (
                        <div className="size-6"></div>
                      )}
                      <p>No. {team.rankNum}</p>
                    </div>
                  </td>
                  <td className={tdStyle + " text-center font-semibold"}>
                    {team.teamName}
                    {isMine && (
                      <span className="ml-2 rounded-full bg-subcolor px-2 py-[1px] text-[11px] text-white">
                        あなた
                      </span>
                    )}
                  </td>
                  <td className={tdStyle}>
                    <div className="flex flex-wrap justify-center gap-1">
                      {team.members.map((m, i) => (
                        <span
                          key={m.userId ?? `masked-${i}`}
                          className="rounded-full bg-gray-100 px-2 py-[2px] text-[12px]"
                        >
                          {m.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className={tdStyle + " text-center"}>
                    <p className="score">{getShortenedScore(team.score)}</p>
                  </td>
                  <td className={tdStyle + " text-center tabular-nums"}>
                    {team.submissionCount}
                  </td>
                  <td
                    className={
                      tdStyle + " text-center text-gray-600 whitespace-nowrap"
                    }
                    title={
                      team.lastSubmittedAt
                        ? new Date(team.lastSubmittedAt).toLocaleString()
                        : undefined
                    }
                  >
                    {formatTimeAgo(team.lastSubmittedAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TeamRanking;
